/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * MarketingLeaderboard Component - Auto-rotating leaderboard for auth pages
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trophy, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import rankingsService from '../../services/rankingsService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('MarketingLeaderboard')

// Get config from env
const getConfig = () => {
  const strategiesStr = import.meta.env.VITE_MARKETING_LEADERBOARD_STRATEGIES || ''
  const strategies = strategiesStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  return {
    strategies,
    limit: parseInt(import.meta.env.VITE_MARKETING_LEADERBOARD_LIMIT, 10) || 15,
    interval: parseInt(import.meta.env.VITE_MARKETING_LEADERBOARD_INTERVAL, 10) || 8000
  }
}

// Mock names for placeholder data when no real rankings exist
const MOCK_NAMES = [
  'MathWizard',
  'QuickSolver',
  'BrainPower',
  'StarStudent',
  'NumberNinja',
  'LogicMaster',
  'SpeedDemon',
  'AcePilot',
  'TopScorer',
  'ChampionMind',
  'SharpThinker',
  'FastLearner'
]

// Generate mock leaderboard data
const generateMockData = (limit, strategyType = 'SAT_MATH') => {
  // Shuffle and pick names
  const shuffled = [...MOCK_NAMES].sort(() => Math.random() - 0.5)
  const selectedNames = shuffled.slice(0, limit)

  // Generate SAT-style scores (e.g., 720/800, 680/750, etc.)
  const baseScore = 650 + Math.floor(Math.random() * 100) // 650-750 base
  const maxScore = 750 + Math.floor(Math.random() * 51) // 750-800 max
  return selectedNames.map((name, idx) => ({
    id: `mock-${idx}`,
    rank: idx + 1,
    user: { publicName: name },
    strategyType,
    primaryScore: Math.round(((baseScore - idx * 15) / maxScore) * 100 * 10) / 10,
    satScore: Math.max(200, baseScore - idx * 15 - Math.floor(Math.random() * 10)),
    satScoreMax: maxScore
  }))
}

/**
 * MarketingLeaderboard Component
 *
 * Displays an auto-rotating leaderboard carousel for configured strategies
 */
const MarketingLeaderboard = () => {
  const config = getConfig()
  const hasStrategies = config.strategies.length > 0

  const [currentIndex, setCurrentIndex] = useState(0)
  const [leaderboards, setLeaderboards] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const currentStrategy = hasStrategies ? config.strategies[currentIndex] : null

  // Fetch leaderboards for all strategies
  useEffect(() => {
    if (!hasStrategies) return

    const fetchLeaderboards = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const results = {}
        let hasAnyRealData = false

        await Promise.all(
          config.strategies.map(async (strategy) => {
            const data = await rankingsService.getLeaderboard(strategy, {
              limit: config.limit,
              period: 'OVERALL_BEST'
            })
            const entries = data.entries || []
            if (entries.length > 0) {
              hasAnyRealData = true
            }
            results[strategy] = entries
          })
        )

        // If no real data exists for any strategy, use mock data
        if (!hasAnyRealData) {
          config.strategies.forEach((strategy) => {
            results[strategy] = generateMockData(config.limit, strategy)
          })
        }

        setLeaderboards(results)
      } catch (err) {
        logger.error('Error fetching leaderboards:', err)
        // On error, fall back to mock data
        const mockResults = {}
        config.strategies.forEach((strategy) => {
          mockResults[strategy] = generateMockData(config.limit, strategy)
        })
        setLeaderboards(mockResults)
        setError(null) // Clear error since we have fallback data
      } finally {
        setIsLoading(false)
      }
    }

    fetchLeaderboards()
  }, [hasStrategies, config.strategies.join(','), config.limit])

  // Auto-rotate through strategies
  useEffect(() => {
    if (!hasStrategies || config.strategies.length <= 1) return

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % config.strategies.length)
    }, config.interval)

    return () => clearInterval(timer)
  }, [hasStrategies, config.strategies.length, config.interval])

  // Don't render if no strategies configured
  if (!hasStrategies) {
    return null
  }

  // Get medal or rank display
  const getRankDisplay = (rank) => {
    if (rank === 1) return { text: '🥇', medal: true }
    if (rank === 2) return { text: '🥈', medal: true }
    if (rank === 3) return { text: '🥉', medal: true }
    return { text: rank.toString(), medal: false }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-white/20 rounded w-2/3 mb-4"></div>
        <div className="space-y-3">
          {[...Array(config.limit)].map((_, i) => (
            <div key={i} className="h-8 bg-white/10 rounded"></div>
          ))}
        </div>
      </div>
    )
  }

  const entries = leaderboards[currentStrategy] || []

  // Still loading or no data yet
  if (entries.length === 0) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mt-auto"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-yellow-300" />
        <AnimatePresence mode="wait">
          <motion.h3
            key={currentStrategy}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.3 }}
            className="font-semibold text-white"
          >
            {rankingsService.getStrategyDisplayName(currentStrategy)} Top Performers
          </motion.h3>
        </AnimatePresence>
      </div>

      {/* Leaderboard entries */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStrategy}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-2"
        >
          {entries.map((entry, idx) => {
            const rank = entry.rank || idx + 1
            const rankInfo = getRankDisplay(rank)

            return (
              <div
                key={entry.id || idx}
                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Rank */}
                  <span className="w-6 text-center text-sm font-medium">
                    {rankInfo.medal ? (
                      <span className="text-base">{rankInfo.text}</span>
                    ) : (
                      <span className="text-primary-200">{rankInfo.text}</span>
                    )}
                  </span>

                  {/* User */}
                  <span className="text-white text-sm truncate max-w-[140px]">
                    {entry.user?.publicName || 'Anonymous'}
                  </span>
                </div>

                {/* Score */}
                <span className="text-primary-100 text-sm font-medium">
                  {rankingsService.formatScore({
                    ...entry,
                    strategyType: entry.strategyType || currentStrategy
                  })}
                </span>
              </div>
            )
          })}
        </motion.div>
      </AnimatePresence>

      {/* Strategy indicator dots */}
      {config.strategies.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4 mb-2">
          {config.strategies.map((strategy, idx) => (
            <button
              key={strategy}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-white w-4' : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`View ${rankingsService.getStrategyDisplayName(strategy)} rankings`}
            />
          ))}
        </div>
      )}

      {/* CTA */}
      <Link
        to="/signup"
        className="flex items-center justify-center gap-2 text-primary-100 hover:text-white text-sm mt-3 py-2 transition-colors group"
      >
        <span>Join and compete</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </motion.div>
  )
}

export default MarketingLeaderboard
